import * as React from 'react';
import type { Variant } from 'framer-motion';
import { AnimatePresence, motion } from 'framer-motion';
import type { ButtonProps } from '../Button/Button';
import Button from '../Button/Button';
import type { PickIncludePartial } from '../../../modules/ts.utils';
import { DEFAULT_TRANSITION } from '../../../styles/animations/framerGlobalConfig';
import cn from '~modules/cn';

import InfoIcon from '../../../assets/icons/v2design/basics/info.svg';
import CheckIcon from '../../../assets/icons/v2design/basics/check.svg';
import AlertIcon from '../../../assets/icons/v2design/basics/alert.svg';
import CloseIcon from '../../../assets/icons/v2design/basics/close.svg';

import './top-banner.scss';

export type BannerType = 'info' | 'success' | 'error' | 'warn';

const ToastIcon: { [K in BannerType]: ReactSvgImage } = {
    info: InfoIcon,
    success: CheckIcon,
    error: AlertIcon,
    warn: InfoIcon
};

export type TopBannerProps = {
    id?: string;
    className?: string;
    type: BannerType;
    label: React.ReactNode;
    icon?: ReactSvgImage;
    buttons?: Array<
        PickIncludePartial<ButtonProps, 'buttonType'> & {
            className?: string;
            content?: React.ReactNode;
            onClick?: React.MouseEventHandler<HTMLButtonElement>;
        }
    >;
    onDismiss?: { (): void };
    showAnimation?: Variant;
    hideAnimation?: Variant;
};

const TopBanner = (props: TopBannerProps) => {
    const Icon = props.icon ?? ToastIcon[props.type];
    return (
        <AnimatePresence initial={false}>
            <motion.div
                className={cn('top-banner', `top-banner--type-${props.type}`, props.className)}
                initial="show"
                animate="show"
                exit="hide"
                transition={DEFAULT_TRANSITION}
                variants={{
                    show: {
                        opacity: 1,
                        height: 'auto',
                        ...(props.showAnimation ?? {})
                    },
                    hide: {
                        opacity: 0,
                        height: 0,
                        ...(props.hideAnimation ?? {})
                    }
                }}
            >
                <div className="top-banner__wrapper">
                    <div className="top-banner__contents">
                        <Icon className="top-banner__icon" />
                        <div>{props.label}</div>
                        {(props.buttons?.length ?? 0) > 0 && (
                            <div className="top-banner__buttons">
                                {props.buttons?.map((button, index) => {
                                    const { buttonType = 'secondary', buttonSize = 'small', content, className, ...otherProps } = button;
                                    return (
                                        <Button
                                            key={index}
                                            {...otherProps}
                                            className={cn('top-banner__button', className)}
                                            buttonType={buttonType}
                                            buttonSize={buttonSize}
                                        >
                                            {content}
                                        </Button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                    <Button className="top-banner__close-button" buttonSize="small" buttonType="transparent" icon={<CloseIcon />} onClick={props.onDismiss} />
                </div>
            </motion.div>
        </AnimatePresence>
    );
};

export default TopBanner;
